import { Component, EventEmitter, Input, OnChanges, Output, SimpleChange } from '@angular/core';
import { ReferenceDataCategoriesConstants } from '../../constants/reference-data-categories';
import { ReferenceDataConstants } from '../../constants/reference-data-constants';
import { ReferenceDataLabels } from '../../constants/reference-data-labels';
import { ReferenceDataOptionLists } from '../../constants/reference-data-option-lists';
import { ReferenceDataService } from '../../services/reference-data.service';

// eslint-disable-next-line no-shadow
enum PasswordStrengthLevel {
  Level1 = 1,
  Level2 = 2,
  Level3 = 3,
  Level4 = 4
}

interface PasswordStrengthMessages {
  passwordStrengthLevel1: string;
  passwordStrengthLevel2: string;
  passwordStrengthLevel3: string;
  passwordStrengthLevel4: string;
}

interface StrengthBarColorClass {
  bar0: string;
  bar1: string;
  bar2: string;
  bar3: string;
}

@Component({
  selector: 'boi-password-strength',
  templateUrl: './password-strength.component.html'
})
export class PasswordStrengthComponent implements OnChanges {
  @Input() public passwordToCheck: string;
  @Input() public showOnlyPasswordFormat = false;
  @Output() readonly passwordPassValidationDependsConfig = new EventEmitter<boolean>();

  public readonly referenceDataLabels = ReferenceDataLabels;
  public strengthBarColorClass: StrengthBarColorClass;
  public strengthMessage: string;
  public symbolsAllowed: string;
  public lowerLettersFlag: boolean;
  public upperLettersFlag: boolean;
  public symbolsFlag: boolean;

  private readonly colorsClass = [
    'password-strength__level--1',
    'password-strength__level--2',
    'password-strength__level--3',
    'password-strength__level--4'
  ];
  private readonly allowPasswordStrengthValidation: boolean;
  private readonly strengthMessages: PasswordStrengthMessages;

  constructor(private readonly referenceDataService: ReferenceDataService) {
    this.symbolsAllowed = this.referenceDataService.getConfigurationValue(
      ReferenceDataCategoriesConstants.CLIENT_ENVIRONMENT_CATEGORY,
      ReferenceDataConstants.PasswordSpecialCharacters
    );

    this.strengthMessages = {
      passwordStrengthLevel1: this.referenceDataService.getOptionListValueByCode(
        ReferenceDataOptionLists.PasswordStrengthLevel,
        ReferenceDataConstants.PasswordStrengthLevel1
      ),
      passwordStrengthLevel2: this.referenceDataService.getOptionListValueByCode(
        ReferenceDataOptionLists.PasswordStrengthLevel,
        ReferenceDataConstants.PasswordStrengthLevel2
      ),
      passwordStrengthLevel3: this.referenceDataService.getOptionListValueByCode(
        ReferenceDataOptionLists.PasswordStrengthLevel,
        ReferenceDataConstants.PasswordStrengthLevel3
      ),
      passwordStrengthLevel4: this.referenceDataService.getOptionListValueByCode(
        ReferenceDataOptionLists.PasswordStrengthLevel,
        ReferenceDataConstants.PasswordStrengthLevel4
      )
    };

    this.allowPasswordStrengthValidation =
      this.referenceDataService.getConfigurationValue(
        ReferenceDataCategoriesConstants.CLIENT_ENVIRONMENT_CATEGORY,
        ReferenceDataConstants.AllowPasswordStrengthValidation
      ) === ReferenceDataOptionLists.AnswerTypeYes
        ? true
        : false;

    this.strengthBarColorClass = {
      bar0: 'password-strength__level--0',
      bar1: 'password-strength__level--0',
      bar2: 'password-strength__level--0',
      bar3: 'password-strength__level--0'
    };
  }

  public ngOnChanges(changes: { [propName: string]: SimpleChange }): void {
    const password = changes.passwordToCheck.currentValue;

    this.setBarColors(4, 'password-strength__level--0');
    const passwordStrength = this.checkStrength(password);

    if (password) {
      if (this.allowPasswordStrengthValidation && passwordStrength >= PasswordStrengthLevel.Level2) {
        this.passwordPassValidationDependsConfig.emit(true);
      } else if (this.allowPasswordStrengthValidation && passwordStrength < PasswordStrengthLevel.Level2) {
        this.passwordPassValidationDependsConfig.emit(false);
      }

      const color = this.getColor(passwordStrength);
      this.setBarColors(color.index, color.color);

      // eslint-disable-next-line default-case
      switch (passwordStrength) {
        case PasswordStrengthLevel.Level1:
          this.strengthMessage = this.strengthMessages.passwordStrengthLevel1;
          break;
        case PasswordStrengthLevel.Level2:
          this.strengthMessage = this.strengthMessages.passwordStrengthLevel2;
          break;
        case PasswordStrengthLevel.Level3:
          this.strengthMessage = this.strengthMessages.passwordStrengthLevel3;
          break;
        case PasswordStrengthLevel.Level4:
          this.strengthMessage = this.strengthMessages.passwordStrengthLevel4;
          break;
      }
    } else {
      this.strengthMessage = '';
    }
  }

  private checkStrength(password: string): number {
    let strength = 0;

    this.lowerLettersFlag = /[a-z]+/.test(password);
    this.upperLettersFlag = /[A-Z]+/.test(password);

    const flags = [this.lowerLettersFlag, this.upperLettersFlag];

    if (this.symbolsAllowed) {
      const symbols = this.symbolsAllowed.split('');
      this.symbolsFlag = symbols.some((symbol: string) => password?.includes(symbol));

      flags.push(this.symbolsFlag);
    }

    const passwordPassAllFlags = flags.every((flag: boolean) => flag);

    if (password?.length >= 1) {
      strength += 1;
    }

    if (passwordPassAllFlags) {
      if (password?.length >= 8) {
        strength += 1;
      }

      if (password?.length >= 11) {
        strength += 1;
      }

      if (password?.length >= 14) {
        strength += 1;
      }
    }

    return strength;
  }

  private getColor(strength: number): { index: number; color: string } {
    let index = 0;

    if (strength === PasswordStrengthLevel.Level1) {
      index = 0;
    } else if (strength === PasswordStrengthLevel.Level2) {
      index = 1;
    } else if (strength === PasswordStrengthLevel.Level3) {
      index = 2;
    } else if (strength === PasswordStrengthLevel.Level4) {
      index = 3;
    } else {
      index = 4;
    }

    return {
      index: index + 1,
      color: this.colorsClass[index]
    };
  }

  private setBarColors(count: number, color: string): void {
    for (let n = 0; n < count; n++) {
      this.strengthBarColorClass[`bar${n}`] = color;
    }
  }
}